import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  alpha,
  useTheme,
} from "@mui/material";
import {
  CheckCircle,
  Schedule,
  Cancel,
  WarningAmber,
  Done,
  TaskAlt,
} from "@mui/icons-material";

const StatusConfirmDialog = ({ open, onClose, onConfirm, status }) => {
  const theme = useTheme();

  const statusConfig = {
    ready: {
      color: { main: "#10b981", dark: "#059669" },
      label: "Mark as Ready",
      icon: CheckCircle,
      title: "Mark Order as Ready?",
      message: "This order will be marked as ready for pickup/serving.",
    },
    preparing: {
      color: { main: "#667eea", dark: "#764ba2" },
      label: "Mark as Preparing",
      icon: Schedule,
      title: "Start Preparing Order?",
      message: "This order will be marked as being prepared in the kitchen.",
    },
    served: {
      color: { main: "#6b7280", dark: "#4b5563" },
      label: "Mark as Served",
      icon: Done,
      title: "Mark Order as Served?",
      message: "This order will be marked as served to the customer.",
    },
    completed: {
      color: { main: "#059669", dark: "#047857" },
      label: "Mark as Completed",
      icon: TaskAlt,
      title: "Complete This Order?",
      message: "This order will be marked as completed and finalized.",
    },
    cancelled: {
      color: { main: "#ef4444", dark: "#dc2626" },
      label: "Cancel Order",
      icon: Cancel,
      title: "Cancel This Order?",
      message: "This action will cancel the order. This cannot be undone.",
    },
  };

  const config = statusConfig[status] || statusConfig.preparing;

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: `0 20px 60px ${alpha(config.color.main, 0.3)}`,
        },
      }}
    >
      {/* Gradient Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${config.color.main} 0%, ${config.color.dark} 100%)`,
          p: 3,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: "absolute",
            top: -40,
            right: -40,
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: alpha("#fff", 0.1),
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -30,
            left: -30,
            width: 100,
            height: 100,
            borderRadius: "50%",
            background: alpha("#fff", 0.08),
          }}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: alpha("#fff", 0.2),
              backdropFilter: "blur(10px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `3px solid ${alpha("#fff", 0.3)}`,
            }}
          >
            <config.icon sx={{ fontSize: 40, color: "white" }} />
          </Box>

          <Typography
            variant="h5"
            sx={{
              color: "white",
              fontWeight: 800,
              textAlign: "center",
              letterSpacing: "-0.5px",
            }}
          >
            {config.title}
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Box
          sx={{
            bgcolor: alpha(config.color.main, 0.08),
            borderRadius: "12px",
            p: 2.5,
            mb: 3,
            border: `1px solid ${alpha(config.color.main, 0.2)}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <WarningAmber
              sx={{ color: config.color.main, fontSize: 24, mt: 0.3 }}
            />
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.primary,
                fontSize: "0.95rem",
                lineHeight: 1.6,
                fontWeight: 500,
              }}
            >
              {config.message}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            fullWidth
            sx={{
              height: 48,
              borderRadius: "12px",
              fontSize: "1rem",
              fontWeight: 700,
              textTransform: "none",
              borderColor: alpha(theme.palette.divider, 0.3),
              color: theme.palette.text.secondary,
              "&:hover": {
                borderColor: alpha(theme.palette.divider, 0.5),
                bgcolor: alpha(theme.palette.divider, 0.05),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            fullWidth
            sx={{
              height: 48,
              borderRadius: "12px",
              fontSize: "1rem",
              fontWeight: 700,
              textTransform: "none",
              background: `linear-gradient(135deg, ${config.color.main} 0%, ${config.color.dark} 100%)`,
              boxShadow: `0 4px 16px ${alpha(config.color.main, 0.4)}`,
              "&:hover": {
                background: `linear-gradient(135deg, ${config.color.dark} 0%, ${config.color.main} 100%)`,
                transform: "translateY(-2px)",
                boxShadow: `0 6px 20px ${alpha(config.color.main, 0.5)}`,
              },
              transition: "all 0.3s ease",
            }}
          >
            Confirm
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default StatusConfirmDialog;
